/**
 * UserPreferencesDialog — lets users save timezone, time format,
 * and notification preferences.
 *
 * Preferences are persisted via PUT /api/auth/me/settings and auto-loaded
 * on next login (the profile endpoint returns preferred_timezone / time_format /
 * notification_preferences).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  FormControl, InputLabel, Select, MenuItem, Typography, Alert,
  Switch, FormControlLabel, Divider, Accordion, AccordionSummary,
  AccordionDetails, Box,
} from '@mui/material';
import { Settings, ExpandMore, Notifications } from '@mui/icons-material';
import { authFetch } from '../api/authFetch';

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Amsterdam',
  'Europe/Dublin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Africa/Nairobi',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
];

interface UserPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the saved timezone value so the parent can auto-fill fields. */
  onSaved?: (timezone: string, timeFormat: string) => void;
}

/** Human-readable labels for notification types. */
const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  assessment_completed: 'Assessment Completed',
  domain_assigned: 'Domain Assigned',
  draft_submitted: 'Draft Report Submitted',
  validation_required: 'Validation Required',
  revision_required: 'Revision Required',
  playback_ready: 'Playback Ready',
  final_report_ready: 'Final Report Ready',
  role_granted: 'Role Granted',
  pending_user: 'New Pending User',
  schedule_call: 'Meeting Scheduled',
};

interface NotifTypePrefs {
  email: boolean;
  in_app: boolean;
}

interface NotificationPreferences {
  email_enabled: boolean;
  in_app_enabled: boolean;
  types: Record<string, NotifTypePrefs>;
}

const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  email_enabled: true,
  in_app_enabled: true,
  types: Object.fromEntries(
    Object.keys(NOTIFICATION_TYPE_LABELS).map((k) => [k, { email: true, in_app: true }]),
  ),
};

const UserPreferencesDialog: React.FC<UserPreferencesDialogProps> = ({
  open, onClose, onSaved,
}) => {
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('24h');
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load current preferences from profile on open
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSuccess(false);
    authFetch('/api/auth/me')
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.user) {
          if (data.user.preferred_timezone) setTimezone(data.user.preferred_timezone);
          if (data.user.time_format) setTimeFormat(data.user.time_format as '12h' | '24h');
          if (data.user.notification_preferences) {
            setNotifPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...data.user.notification_preferences });
          }
        }
      })
      .catch(() => { /* best effort */ });
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/auth/me/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferred_timezone: timezone,
          time_format: timeFormat,
          notification_preferences: notifPrefs,
        }),
      });
      if (res.ok) {
        setSuccess(true);
        onSaved?.(timezone, timeFormat);
        setTimeout(() => onClose(), 1200);
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to save preferences.');
      }
    } catch {
      setError('Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings fontSize="small" /> User Preferences
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set your preferred timezone and time display format. These will be
          used when scheduling playback sessions and displaying dates.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Preferences saved.</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Preferred Timezone</InputLabel>
            <Select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              label="Preferred Timezone"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Time Format</InputLabel>
            <Select
              value={timeFormat}
              onChange={(e) => setTimeFormat(e.target.value as '12h' | '24h')}
              label="Time Format"
            >
              <MenuItem value="24h">24-hour (e.g. 16:30)</MenuItem>
              <MenuItem value="12h">12-hour (e.g. 4:30 PM)</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          {/* Notification Preferences */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Notifications fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Notification Preferences
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={notifPrefs.email_enabled}
                onChange={(e) => setNotifPrefs((p) => ({ ...p, email_enabled: e.target.checked }))}
              />
            }
            label="Enable email notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={notifPrefs.in_app_enabled}
                onChange={(e) => setNotifPrefs((p) => ({ ...p, in_app_enabled: e.target.checked }))}
              />
            }
            label="Enable in-app notifications"
          />

          <Accordion variant="outlined" sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Per-type notification settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => {
                  const typePrefs = notifPrefs.types[key] || { email: true, in_app: true };
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={typePrefs.email}
                            disabled={!notifPrefs.email_enabled}
                            onChange={(e) => setNotifPrefs((p) => ({
                              ...p,
                              types: {
                                ...p.types,
                                [key]: { ...p.types[key], email: e.target.checked },
                              },
                            }))}
                          />
                        }
                        label="Email"
                        sx={{ mr: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={typePrefs.in_app}
                            disabled={!notifPrefs.in_app_enabled}
                            onChange={(e) => setNotifPrefs((p) => ({
                              ...p,
                              types: {
                                ...p.types,
                                [key]: { ...p.types[key], in_app: e.target.checked },
                              },
                            }))}
                          />
                        }
                        label="In-app"
                      />
                    </Box>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPreferencesDialog;
